import classNames from 'classnames'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import { useMemo } from 'react'

import styles from './side-by-side.module.scss'
import type { VideoPresetType, VideoSourceType } from '@app-types/video'
import type { HeadingTag } from '@components/heading'
import { Container } from '@components/layout/grid'
import { Markdown } from '@components/markdown'
import { Text } from '@components/text'
import { Video } from '@components/video'
import { ActionButton } from '@contentful-components/action-button'
import type { SideBySideFragment } from '@graphql/fragments/SideBySideFragment.graphql'
import { usePersonalizations } from '@lib/hooks/usePersonalizations'
import { renderTemplate } from '@lib/utils/text'
import variables, { type Variable } from '@styles/variables'

/**
 * Displays a content block with a title, text, image,and/or link button next to some sort of media, such as a video or image.
 */

export const SideBySide: FunctionComponent<Props> = ({
    anchorId = '',
    heading,
    headingColor,
    copy,
    copyColor,
    supportingCopy,
    supportingCopyColor,
    button,
    secondaryButton,
    image,
    video,
    mediaPosition,
    mediaPositionVertical = 'bottom',
    variant = 'default',
    headingTag = 'h2',
    spacing = 'default',
    backgroundColor = 'black',
}) => {
    const personalizations = usePersonalizations()

    const visualElement = useMemo(() => {
        if (video) {
            return (
                <Video
                    {...video}
                    preset={(video.preset as VideoPresetType) ?? 'gif'}
                    poster={video.poster?.source}
                    alt={video.alt as string}
                    sources={video?.files?.items as VideoSourceType[]}
                    className={styles.media}
                />
            )
        }

        if (image?.url) {
            return (
                <Image
                    className={classNames(styles.media, 'responsive-image')}
                    src={image.url}
                    width={image.width}
                    height={image.height}
                    alt={image?.alt ?? ''}
                    priority
                />
            )
        }

        return null
    }, [video, image])

    return (
        <div
            style={{ backgroundColor: variables[backgroundColor as Variable] }}
            id={anchorId}
        >
            <Container
                className={classNames(
                    styles['container'],
                    styles[`${spacing}-spacing`],
                )}
            >
                <div className={styles[`row`]}>
                    <div
                        className={classNames(
                            styles['column'],
                            styles['media-column'],
                            styles[`column-${mediaPosition}`],
                            styles[
                                `column-order-${
                                    mediaPosition === 'left' ? 0 : 2
                                }`
                            ],
                            styles[
                                `column-order-mobile-${mediaPositionVertical}`
                            ],
                        )}
                    >
                        <div
                            className={classNames(
                                styles[`column-inner`],
                                styles[`column-inner--${variant}`],
                            )}
                        >
                            {visualElement}
                        </div>
                    </div>
                    <div
                        className={classNames(
                            styles['column'],
                            styles['copy-column'],
                            styles[
                                `column-${
                                    mediaPosition === 'left' ? 'right' : 'left'
                                }`
                            ],
                        )}
                    >
                        <div
                            className={classNames(
                                styles[`column-inner`],
                                styles[`column-inner--${variant}`],
                            )}
                        >
                            <Markdown
                                tag={headingTag}
                                typeStyle={{
                                    _: 'heading-md',
                                    md: 'heading-l',
                                }}
                                markdown={renderTemplate(
                                    heading,
                                    personalizations,
                                )}
                                color={headingColor}
                                accentColor='primary'
                            />
                            <div className={styles['copy-container']}>
                                <Markdown
                                    typeStyle={{ _: 'body-lg', md: 'body-md' }}
                                    markdown={renderTemplate(
                                        copy,
                                        personalizations,
                                    )}
                                    color={copyColor}
                                />
                            </div>
                            {button && (
                                <div className={styles.buttons}>
                                    {!!button && <ActionButton {...button} />}
                                    {!!secondaryButton && (
                                        <ActionButton
                                            preset='secondary'
                                            {...secondaryButton}
                                        />
                                    )}
                                </div>
                            )}
                            {supportingCopy && (
                                <Text
                                    typeStyle={{
                                        _: 'body-lg',
                                    }}
                                    tag='div'
                                    className={styles['supporting-copy']}
                                    color={supportingCopyColor}
                                >
                                    {supportingCopy}
                                </Text>
                            )}
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    )
}

export default SideBySide

type Props = Omit<SideBySideFragment, 'sys' | 'type'> & {
    headingTag?: HeadingTag
}
